<template>
  <div :class="{'hide': !showSummary}">
    <div
      v-if="showSummary"
      class="entry"
    >
      <div class="entry__header uk-flex-space-between uk-flex uk-flex-middle uk-margin-bottom">
        <div
          v-if="entry.data.SKU"
          class="entry__sku"
        >
          Entry: #{{ entry.data.SKU }}
        </div>
        <small
          :class="'entry__status entry__status--' + entry.data.approved"
          @click="statusClickHandler"
        >{{ textStatus }}</small>
      </div>

      <h1>
        {{ entry.getFullName() }}
      </h1>

      <p
        v-if="division"
      >
        <span v-if="!entry.isSpectator()">Division: </span>{{ division.getTitleLabel() }}
      </p>

      <p
        v-if="entry.data.hyung"
      >
        Hyung: {{ entry.data.hyung }}
      </p>

      <div class="uk-flex uk-flex-middle uk-margin-top">
        <a
          v-if="editable"
          class="entry__button"
          href="#"
          @click.prevent="openEdit"
        >edit</a>
        <a
          v-else
          class="entry__button"
          href="#"
          @click.prevent="openEdit"
        >view</a>

        <div
          v-if="!entry.isWithdrawn()"
          style="margin-left: auto"
        >
          <span
            v-if="videoIsProcessing"
            class="entry__statusText"
          >Check back later to view your video submission.</span>
          <span
            v-if="videoIsPendingCheckout"
            class="entry__statusText"
          >Video uploaded (pending checkout)</span>

          <a
            v-if="videoIsReady"
            href="#"
            class="entry__button entry__previewVideo"
            @click.prevent="previewVideo"
          ><span class="uk-icon-film" /> Preview video</a>

          <a
            v-if="videoIsMissing"
            href="#"
            class="entry__button entry__uploadVideo"
            @click.prevent="openFormAtStep = STEPS.UPLOAD; openEdit()"
          ><span class="uk-icon-exclamation-triangle" /> Upload your video</a>
        </div>
      </div>
      <!--      <span
        v-for="step in STEPS"
        :key="step"

        @click="openFormAtStep = step; openEdit()"
      >
        <span
          v-if="[STEPS.GUARDIAN, STEPS.WAIVER].indexOf(step) === -1"
          class="entry__button"
        >{{ step }}</span>
      </span>-->
    </div>

    <EntryForm
      v-if="showModal"
      :start-step="openFormAtStep"
      :entry="entry"
      :event-id="entry.data.event_id"
      :modal-top="triggerPos"
      @close-modal="closeModal"
      @deleted="removeComponent"
      @select-files="$refs.uploader.click()"
      @create-entry="$emit('create-entry')"
      @preview-video="closeAndPreviewVideo"
    />

    <PreviewVideo
      v-if="showVideo"
      :entry="entry"
      @close="showVideo = false"
    />

    <div ref="uploader" />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import EventEntryModel from '../../models/EventEntryModel';
import EntryForm from "./EntryForm.vue";
import DivisionModel from "../../models/DivisionModel";
import UploadedFileModel, { create as createUploadedFile } from "../../models/UploadedFile";
import { queueFile } from "../../lib/upload";
import {STEPS} from "./steps";
import PreviewVideo from "./PreviewVideo.vue";
import EventModel from "../../models/EventModel";

@Component({
  components: {
    EntryForm,
    PreviewVideo,
  }
})
export default class EntrySummary extends Vue {
  @Prop() entry!: EventEntryModel;

  showModal = false;

  openFormAtStep = '';

  STEPS = STEPS;

  fileSelector!: moxie.file.FileInput;

  fileReferences!: string[];

  showVideo = false;

  triggerPos = 0;

  created(): void {
    this.showModal = !this.showSummary;
  }

  mounted() : void {
    this.fileSelector = new moxie.file.FileInput({
      browse_button: this.$refs.uploader,
      multiple: false,
      filters: {
        /*mime_types: [
          {title: "Video files", extensions: "ogg,mp4,m4v,mkv,avi,mov,flv,mpeg,3gp,wmv,webm,3gp,mkv,dv,mpg,asf,3g2"}
        ]*/
        mime_types: "video/mp4,video/mkv,video/mov"
      }
    });

    this.fileSelector.onchange = () => {
      for (const i in this.fileSelector.files) {
        const inputFile = this.fileSelector.files[i];
        const newFile = createUploadedFile({
          original_name: inputFile.name
        });
        this.$store.dispatch('files/create', newFile).then((file: UploadedFileModel) => {
          if (file.data.reference) {
            queueFile('entries', inputFile, {
              upload_url: BASE_URI + '/api/files/' + file.data.reference,
              upload_type: 'file',
              file_id: file.data.reference,
            });

            this.$store.dispatch('events/files/add', {
              entry_ref: this.entry.data.reference,
              file_ref: file.data.reference,
            });
          }
        });
      }
    };

    this.fileSelector.init();
  }

  get videoIsProcessing(): boolean {
    return this.entry.isPaid() && !this.entry.hasCompletedVideo() && !this.entry.isMissingVideo() && !this.divisionSkipUploads;
  }

  get videoIsPendingCheckout(): boolean {
    return this.entry.isUnpaid() && !this.entry.hasCompletedVideo() && !this.entry.isMissingVideo() && !this.divisionSkipUploads;
  }

  get videoIsReady(): boolean {
    return this.entry.hasCompletedVideo() && !this.divisionSkipUploads && !this.entry.isWithdrawn();
  }

  get videoIsMissing(): boolean {
    return this.entry.isMissingVideo() && !this.divisionSkipUploads && !this.event.data.deadline_replace_video_passed && !this.entry.isWithdrawn();
  }

  get editable(): boolean {
    if (!this.event.data.deadline_edit_passed && !this.entry.isWithdrawn()) {
      return true;
    }

    return false;
  }

  get event(): EventModel {
    return this.$store.getters['events/byId'](this.entry.data.event_id);
  }

  get divisionSkipUploads(): boolean {
    return this.division?.data.skip_uploads || false;
  }

  get textStatus(): string {
    if (this.entry.isPaid() && (this.entry.isSpectator() || this.division?.data.skip_uploads)) {
      return 'OK';
    }

    if (this.entry.isPaid() && !this.entry.isMissingVideo()) {
      return 'successful upload';
    }

    if (this.entry.data.approved === 'withdrawn') {
      return 'withdrawn';
    }

    if (this.entry.data.approved === 'unpaid') {
      return 'pending checkout';
    }

    return 'incomplete';
  }

  statusClickHandler(): void {
    if (this.entry.data.approved === 'unpaid') {
      this.$store.dispatch('events/cart/showCart', true);
    }
  }

  openEdit(e: MouseEvent): void {
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }

  get division(): DivisionModel {
    return this.$store.getters['events/divisions/getById'](this.entry.data.division_id);
  }

  get showSummary(): boolean {
    if (this.entry && this.entry.data.id) {
      return true;
    }

    return false;
  }

  previewVideo(): void {
    this.showVideo = !this.showVideo;
  }

  closeAndPreviewVideo(): void {
    this.closeModal();
    this.previewVideo();
  }

  removeComponent(): void {

  }
}
</script>

<style scoped>
.entry {
  background: rgb(35, 89, 131);
  background: linear-gradient(167deg, rgb(50 41 144) 40%, rgba(13, 6, 97, 1) 80%);
  color: #FFF;
  padding: 1em;
  border-radius: 5px;
}
.entry h1 {
  font-size: 1.5em;
}
.entry__sku {
  font-weight: bold;
}
.entry__button {
  padding: 0.4em;
  border-radius: 0.4em;
  background: #fff;
  color: rgba(13, 6, 97, 1);
  display: inline-block;
}
.entry__status {
  text-transform: uppercase;
  white-space: nowrap;
  padding: 0.5em;
  border-radius: 5px;
  background: rgba(255, 255, 255, .1);
  font-weight: bold;
}
.entry__status--incomplete, .entry__status--unpaid {
  background: #ffb121;
  color: #3a2500;
}
.entry__status--yes {
  background: #215d21;
  color: #efffef;
}
.entry__uploadVideo, .entry__previewVideo, .entry__statusText {
  margin-left: auto;
  text-align: right;
}
.entry__uploadVideo {
  text-decoration: none;
  background: #d81a1a;
  color: #fff;
}
.entry__uploadVideo:hover {
  background: #aa1515;
}

</style>
